import { apiVersion } from '../apis.const';
import { transformResponse } from '../apis.util';
import { baseApi } from '../base.api';

/**
 * Create APIs
 */
export const buildingsApi = baseApi.injectEndpoints({
  overrideExisting: false,

  endpoints: (build) => ({
    fetchBuildings: build.query<FetchBuildingsRes, FetchBuildingsReq>({
      query: ({ orgSlug }) => ({
        url: `/${orgSlug}/${apiVersion}/buildings`,
      }),
      transformResponse,
      providesTags: ['Buildings'],
    }),

    fetchBuilding: build.query<FetchBuildingRes, FetchBuildingReq>({
      query: ({ orgSlug, id }) => ({
        url: `/${orgSlug}/${apiVersion}/buildings/${id}`,
      }),
      transformResponse,
      providesTags: (result, error, { id }) => [{ type: 'Building', id }],
    }),

    createBuilding: build.mutation<CreateBuildingRes, CreateBuildingReq>({
      query: ({ orgSlug, building }) => ({
        url: `/${orgSlug}/${apiVersion}/buildings`,
        method: 'POST',
        body: building,
      }),
      transformResponse,
      invalidatesTags: ['Buildings'],
    }),

    updateBuilding: build.mutation<UpdateBuildingRes, UpdateBuildingReq>({
      query: ({ orgSlug, id, building }) => ({
        url: `/${orgSlug}/${apiVersion}/buildings/${id}`,
        method: 'POST',
        body: building,
      }),
      transformResponse,
      invalidatesTags: (result, error, { id }) => [{ type: 'Building', id }, 'GetDecal'],
    }),

    deleteBuilding: build.mutation<DeleteBuildingRes, DeleteBuildingReq>({
      query: ({ orgSlug, id }) => ({
        url: `/${orgSlug}/${apiVersion}/buildings/${id}`,
        method: 'DELETE',
      }),
      transformResponse,
      invalidatesTags: ['Buildings'],
    }),

    fetchBuildingTargetReport: build.query<FetchBuildingTargetReportRes, FetchBuildingTargetReportReq>({
      query: ({ orgSlug, id, start, end }) => ({
        url: `/${orgSlug}/${apiVersion}/building/${id}/target-report?start=${start}&end=${end}`,
      }),
      transformResponse,
    }),

    fetchBuildingServiceZoneReport: build.query<FetchBuildingServiceZoneReportRes, FetchBuildingServiceZoneReportReq>({
      query: ({ orgSlug, id, start, end }) => ({
        url: `/${orgSlug}/${apiVersion}/building/${id}/service-zone-report?start=${start}&end=${end}`,
      }),
      transformResponse,
    }),

    fetchBuildingZoneSetupReport: build.query<FetchBuildingZoneSetupReportRes, FetchBuildingZoneSetupReportReq>({
      query: ({ orgSlug, id }) => ({
        url: `/${orgSlug}/${apiVersion}/building/${id}/zone-setup-report`,
      }),
      transformResponse,
    }),

    deleteBuildingThumbnail: build.mutation<DeleteBuildingThumbnailRes, DeleteBuildingThumbnailReq>({
      query: ({ orgSlug, id }) => ({
        url: `/${orgSlug}/${apiVersion}/buildings/${id}/thumbnail`,
        method: 'DELETE',
      }),
      transformResponse,
    }),

    deleteBuildingLogo: build.mutation<DeleteBuildingLogoRes, DeleteBuildingLogoReq>({
      query: ({ orgSlug, id }) => ({
        url: `/${orgSlug}/${apiVersion}/buildings/${id}/logo`,
        method: 'DELETE',
      }),
      transformResponse,
    }),

    deleteBuildingArea: build.mutation<DeleteBuildingAreaRes, DeleteBuildingAreaReq>({
      query: ({ orgSlug, id, areaId }) => ({
        url: `/${orgSlug}/${apiVersion}/buildings/${id}/areas/${areaId}`,
        method: 'DELETE',
      }),
      transformResponse,
    }),
  }),
});

/**
 * Hooks
 */
export const {
  useFetchBuildingsQuery,
  useFetchBuildingQuery,
  useCreateBuildingMutation,
  useUpdateBuildingMutation,
  useDeleteBuildingMutation,
  useFetchBuildingTargetReportQuery,
  useFetchBuildingServiceZoneReportQuery,
  useFetchBuildingZoneSetupReportQuery,
  useDeleteBuildingThumbnailMutation,
  useDeleteBuildingLogoMutation,
  useDeleteBuildingAreaMutation,
} = buildingsApi;

/**
 * Types
 */
type OrgSlug = {
  orgSlug: string;
};
type OrgSlugAndId = {
  orgSlug: string;
  id: number;
};
type FetchBuildingsRes = any;
type FetchBuildingsReq = OrgSlug;
type FetchBuildingRes = any;
type FetchBuildingReq = OrgSlugAndId;
type CreateBuildingRes = any;
type CreateBuildingReq = OrgSlug & {
  building: FormData;
};
type UpdateBuildingRes = any;
type UpdateBuildingReq = OrgSlugAndId & {
  building: FormData;
};
type DeleteBuildingRes = any;
type DeleteBuildingReq = OrgSlugAndId;
type FetchBuildingTargetReportRes = any;
type FetchBuildingTargetReportReq = OrgSlugAndId & {
  start: string;
  end: string;
};
type FetchBuildingServiceZoneReportRes = any;
type FetchBuildingServiceZoneReportReq = OrgSlugAndId & {
  start: string;
  end: string;
};
type FetchBuildingZoneSetupReportRes = any;
type FetchBuildingZoneSetupReportReq = OrgSlugAndId;
type DeleteBuildingThumbnailRes = any;
type DeleteBuildingThumbnailReq = OrgSlugAndId;
type DeleteBuildingLogoRes = any;
type DeleteBuildingLogoReq = OrgSlugAndId;
type DeleteBuildingAreaRes = any;
type DeleteBuildingAreaReq = OrgSlugAndId & { areaId: number };
